/** PROTOTYPE hook: real per-spread price from the engine (all modules in one call). Debounced, cancellable. */
import { useEffect, useState } from 'react';
import { Spread, ProjectState } from './spreadSplitData';
import { fetchSpreadPrice, buildSpreadRequest, weldingMonths, WeldMode, SpreadPriceResult } from './spreadPricingApi';

export type SpreadPriceState =
  | { status: 'idle' | 'loading' }
  | { status: 'done'; result: SpreadPriceResult }
  | { status: 'error'; error: string };

export function useSpreadPrice(project: ProjectState, spread: Spread | null, mode: WeldMode, mobilizing: boolean): SpreadPriceState {
  const [state, setState] = useState<SpreadPriceState>({ status: 'idle' });
  const hasSpread = !!spread;
  // The request body is the cache key: refetch whenever anything priced changes
  // (machines, mills / vendors / contractors, add-ons, overrides, duration, mode).
  const bodyKey = spread ? JSON.stringify(buildSpreadRequest(project, spread, weldingMonths(spread), { mode, mobilizing })) : '';

  useEffect(() => {
    if (!spread) { setState({ status: 'idle' }); return; }
    let cancelled = false;
    setState({ status: 'loading' });
    const t = setTimeout(async () => {
      try {
        const result = await fetchSpreadPrice(project, spread, { mode, mobilizing });
        if (!cancelled) setState({ status: 'done', result });
      } catch (e) {
        if (!cancelled) setState({ status: 'error', error: e instanceof Error ? e.message : 'Request failed' });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSpread, bodyKey]);

  return state;
}
